import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DotsThreeOutlineVertical, Plus, Trash } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { PortalWrapper } from 'src/components/PortalWrapper';
import { SVG_SIZE_S } from 'src/constants';
import { useEditorTable } from 'src/hooks';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import styles from './TableRowControlsOverlay.module.scss';

export const TableRowControlsOverlay = () => {
  const [editor] = useLexicalComposerContext();
  const { addRowBelow, deleteRow } = useEditorTable();

  const [targetRow, setTargetRow] = useState<HTMLElement | null>(null);
  const [isHoveringRow, setIsHoveringRow] = useState<boolean>(true);
  const [isHoveringButton, setIsHoveringButton] = useState<boolean>(true);

  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      setIsHoveringRow(false);
      setIsHoveringButton(false);
      setTargetRow(null);
    };

    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const el = (e.target as HTMLElement)?.closest('[data-lexical-table-row]');
      if (el) {
        setTargetRow(el as HTMLElement);
        setIsHoveringRow(true);
      } else {
        setIsHoveringRow(false);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  useEffect(() => {
    if (!isHoveringRow && !isHoveringButton) {
      setTargetRow(null);
    }
  }, [isHoveringRow, isHoveringButton]);

  if (!targetRow) {
    return null;
  }

  const rect = targetRow.getBoundingClientRect();
  const top = rect.top + window.scrollY;
  const left = rect.left + window.scrollX - 15;

  return (
    <PortalWrapper>
      <div
        ref={observerRef}
        style={{
          position: 'absolute',
          top: top + rect.height / 2 - 20,
          left,
          zIndex: 1000,
        }}
        onMouseEnter={() => setIsHoveringButton(true)}
        onMouseLeave={() => setIsHoveringButton(false)}
      >
        <Menu
          gap={8}
          align="start"
          transition
          menuClassName={styles.menu}
          menuButton={
            <MenuButton data-testid="snowman-menu">
              <span className={styles.menuButton}>
                <DotsThreeOutlineVertical
                  size={SVG_SIZE_S}
                  color="currentColor"
                  alt="Conversation menu"
                />
              </span>
            </MenuButton>
          }
        >
          <MenuItem
            data-testid="delete-conversation-btn"
            onClick={() => {
              editor.update(() => {
                addRowBelow(
                  targetRow.getAttribute('data-lexical-table-row') as string,
                );
              });
            }}
          >
            <Plus size={SVG_SIZE_S} color="currentColor" /> Add row below
          </MenuItem>
          <MenuItem
            data-testid="delete-conversation-btn"
            onClick={() => {
              editor.update(() => {
                deleteRow(
                  targetRow.getAttribute('data-lexical-table-row') as string,
                );
              });
            }}
          >
            <Trash size={SVG_SIZE_S} color="currentColor" /> Delete
          </MenuItem>
        </Menu>
      </div>
    </PortalWrapper>
  );
};
